import { useState } from 'react';
import PageSEO from '../../components/feature/PageSEO';
import Footer from '../home/components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://arvakon.com';

const contactJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Technical Inquiry – Arvakon',
    url: `${siteUrl}/contact`,
    description: 'Submit a technical service inquiry to Arvakon for manufacturing surveillance, welding oversight, NDT witness, material traceability, and protective coating inspection services.',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Does Arvakon provide certification services?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'No. Arvakon provides independent technical verification and reporting services. Certification, regulatory approval, and final acceptance authority remain with the designated certification body or equipment purchaser.',
        },
      },
      {
        '@type': 'Question',
        name: 'Does Arvakon act as a notified body under PED?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'No. Arvakon does not function as a notified body under PED 2014/68/EU and does not issue regulatory approvals.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can Arvakon perform surveillance at international fabrication facilities?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Services are structured to provide independent technical oversight at fabrication sites based on client-defined scope and applicable standards.',
        },
      },
      {
        '@type': 'Question',
        name: 'What documentation is provided after verification activities?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Structured inspection reports, documented observations, traceability verification records, and testing witness documentation are provided in accordance with client requirements.',
        },
      },
    ],
  },
];

const PUBLIC_EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com',
  'icloud.com', 'me.com', 'mac.com', 'aol.com', 'protonmail.com',
  'proton.me', 'mail.com', 'gmx.com', 'gmx.net', 'yandex.com',
  'yandex.ru', 'zoho.com', 'inbox.com', 'fastmail.com', 'tutanota.com',
  'msn.com', 'qq.com', '163.com', '126.com', 'sina.com',
];

function isPublicEmailDomain(email: string): boolean {
  const parts = email.split('@');
  if (parts.length !== 2) return false;
  const domain = parts[1].toLowerCase();
  return PUBLIC_EMAIL_DOMAINS.includes(domain);
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function ContactPage() {
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    position: '',
    email: '',
    phone: '',
    projectLocation: '',
    industrySector: '',
    equipmentCategory: '',
    applicableCodes: '',
    requiredServices: [] as string[],
    projectTimeline: '',
    additionalInfo: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [emailWarning, setEmailWarning] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error on change
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Live email validation
    if (name === 'email') {
      if (value && isValidEmail(value) && isPublicEmailDomain(value)) {
        setEmailWarning('Please use a corporate email address for technical inquiries.');
      } else {
        setEmailWarning('');
      }
    }
  };

  const handleCheckboxChange = (service: string) => {
    setFormData(prev => {
      const updated = prev.requiredServices.includes(service)
        ? prev.requiredServices.filter(s => s !== service)
        : [...prev.requiredServices, service];
      if (updated.length > 0 && errors.requiredServices) {
        setErrors(e => ({ ...e, requiredServices: '' }));
      }
      return { ...prev, requiredServices: updated };
    });
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.companyName.trim()) newErrors.companyName = 'Company Name is required.';
    if (!formData.contactPerson.trim()) newErrors.contactPerson = 'Contact Person is required.';
    if (!formData.position.trim()) newErrors.position = 'Position / Department is required.';

    if (!formData.email.trim()) {
      newErrors.email = 'Email Address is required.';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    } else if (isPublicEmailDomain(formData.email)) {
      newErrors.email = 'Please use a corporate email address for technical inquiries.';
    }

    if (!formData.industrySector) newErrors.industrySector = 'Industry Sector is required.';
    if (formData.requiredServices.length === 0) newErrors.requiredServices = 'Please select at least one required service.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('companyName', formData.companyName);
      formBody.append('contactPerson', formData.contactPerson);
      formBody.append('position', formData.position);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('projectLocation', formData.projectLocation);
      formBody.append('industrySector', formData.industrySector);
      formBody.append('equipmentCategory', formData.equipmentCategory);
      formBody.append('applicableCodes', formData.applicableCodes);
      formBody.append('requiredServices', formData.requiredServices.join(', '));
      formBody.append('projectTimeline', formData.projectTimeline);
      formBody.append('additionalInfo', formData.additionalInfo);

      const response = await fetch('https://readdy.ai/api/form/d6iqafefjdhm2lq4suj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          contactPerson: '',
          position: '',
          email: '',
          phone: '',
          projectLocation: '',
          industrySector: '',
          equipmentCategory: '',
          applicableCodes: '',
          requiredServices: [],
          projectTimeline: '',
          additionalInfo: '',
        });
        setEmailWarning('');
        setErrors({});
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const industrySectors = [
    'Renewable Energy Infrastructure',
    'Pressure Equipment Manufacturing',
    'Structural Steel Fabrication',
    'EPC Multi-Supplier Projects',
    'Energy Storage Systems (BESS)',
    'Heavy Industrial Equipment',
    'Other',
  ];

  const equipmentCategories = [
    'Pressure Vessels',
    'Heat Exchangers',
    'Storage Tanks',
    'Structural Steel Components',
    'Piping Systems',
    'Wind Turbine Components',
    'BESS Enclosures',
    'Other',
  ];

  const servicesList = [
    'Independent Manufacturing Surveillance',
    'Welding Oversight and Procedure Review',
    'Non-Destructive Testing Witness & Review',
    'Material Traceability Verification',
    'Protective Coating Inspection',
    'Supplier Surveillance & Sub-tier Oversight',
  ];

  const fieldClass = (name: string) =>
    `w-full px-4 py-3 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-industrial-steel/30 focus:border-industrial-steel transition-colors ${
      errors[name] ? 'border-red-400 bg-red-50/30' : 'border-slate-300'
    }`;

  return (
    <div className="min-h-screen bg-white">
      <PageSEO
        title="Technical Inquiry – Arvakon | Contact for Manufacturing Verification Services"
        description="Submit a technical service inquiry to Arvakon for manufacturing surveillance, welding oversight, NDT witness, material traceability verification, and protective coating inspection services."
        keywords="technical inquiry, contact Arvakon, manufacturing verification inquiry, welding oversight request, NDT witness services, coating inspection"
        canonical={`${siteUrl}/contact`}
        jsonLd={contactJsonLd}
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <a href="/" className="text-dark-primary text-xl font-semibold tracking-tight">
            ARVAKON
          </a>
          <nav className="flex items-center gap-8">
            <a href="/" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">Home</a>
            <a href="/services" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">Services</a>
            <a href="/standards" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">Standards</a>
            <a href="/projects" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">Industry Applications</a>
            <a href="/about" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">About</a>
            <a href="/contact" className="text-sm text-dark-primary font-medium">Contact</a>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 pb-24 px-8">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="mb-16">
            <h1 className="font-serif text-4xl text-dark-primary mb-6 tracking-tight">
              Technical Inquiry
            </h1>
            <div className="space-y-4 text-slate-600 leading-7">
              <p>
                Arvakon accepts technical service inquiries related to manufacturing surveillance, welding oversight, non-destructive testing witness activities, material traceability verification, and protective coating inspection.
              </p>
              <p>
                All inquiries are reviewed based on scope, applicable standards, project timeline, and operating jurisdiction.
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} data-readdy-form id="inquiry-form" className="space-y-8" noValidate>

            {/* Company Information */}
            <div className="space-y-6">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 pb-3 border-b border-slate-200">
                Company Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-slate-700 mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    className={fieldClass('companyName')}
                  />
                  {errors.companyName && (
                    <p className="mt-1.5 text-xs text-red-600">{errors.companyName}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="contactPerson" className="block text-sm font-medium text-slate-700 mb-2">
                    Contact Person <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contactPerson"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleInputChange}
                    className={fieldClass('contactPerson')}
                  />
                  {errors.contactPerson && (
                    <p className="mt-1.5 text-xs text-red-600">{errors.contactPerson}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="position" className="block text-sm font-medium text-slate-700 mb-2">
                  Position / Department <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="position"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  className={fieldClass('position')}
                />
                {errors.position && (
                  <p className="mt-1.5 text-xs text-red-600">{errors.position}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={fieldClass('email')}
                  />
                  {emailWarning && !errors.email && (
                    <p className="mt-1.5 text-xs text-amber-700 flex items-start gap-1.5">
                      <i className="ri-error-warning-line mt-0.5 shrink-0"></i>
                      {emailWarning}
                    </p>
                  )}
                  {errors.email && (
                    <p className="mt-1.5 text-xs text-red-600">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={fieldClass('phone')}
                  />
                </div>
              </div>
            </div>

            {/* Project Information */}
            <div className="space-y-6">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 pb-3 border-b border-slate-200">
                Project Information
              </h2>

              <div>
                <label htmlFor="projectLocation" className="block text-sm font-medium text-slate-700 mb-2">
                  Project Location
                </label>
                <input
                  type="text"
                  id="projectLocation"
                  name="projectLocation"
                  value={formData.projectLocation}
                  onChange={handleInputChange}
                  className={fieldClass('projectLocation')}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="industrySector" className="block text-sm font-medium text-slate-700 mb-2">
                    Industry Sector <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="industrySector"
                    name="industrySector"
                    value={formData.industrySector}
                    onChange={handleInputChange}
                    className={`${fieldClass('industrySector')} cursor-pointer`}
                  >
                    <option value="">Select Industry Sector</option>
                    {industrySectors.map(sector => (
                      <option key={sector} value={sector}>{sector}</option>
                    ))}
                  </select>
                  {errors.industrySector && (
                    <p className="mt-1.5 text-xs text-red-600">{errors.industrySector}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="equipmentCategory" className="block text-sm font-medium text-slate-700 mb-2">
                    Equipment Category
                  </label>
                  <select
                    id="equipmentCategory"
                    name="equipmentCategory"
                    value={formData.equipmentCategory}
                    onChange={handleInputChange}
                    className={`${fieldClass('equipmentCategory')} cursor-pointer`}
                  >
                    <option value="">Select Equipment Category</option>
                    {equipmentCategories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="applicableCodes" className="block text-sm font-medium text-slate-700 mb-2">
                  Applicable Codes / Standards
                </label>
                <input
                  type="text"
                  id="applicableCodes"
                  name="applicableCodes"
                  placeholder="e.g., ASME BPVC Section VIII, ISO 3834, EN 13445"
                  value={formData.applicableCodes}
                  onChange={handleInputChange}
                  className={fieldClass('applicableCodes')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  Required Services <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  {servicesList.map(service => (
                    <label key={service} className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={formData.requiredServices.includes(service)}
                        onChange={() => handleCheckboxChange(service)}
                        className="mt-1 w-4 h-4 border-slate-300 rounded text-industrial-steel focus:ring-2 focus:ring-industrial-steel/30 cursor-pointer"
                      />
                      <span className="text-sm text-slate-700 group-hover:text-dark-primary transition-colors">
                        {service}
                      </span>
                    </label>
                  ))}
                </div>
                {errors.requiredServices && (
                  <p className="mt-2 text-xs text-red-600">{errors.requiredServices}</p>
                )}
              </div>

              <div>
                <label htmlFor="projectTimeline" className="block text-sm font-medium text-slate-700 mb-2">
                  Project Timeline
                </label>
                <input
                  type="text"
                  id="projectTimeline"
                  name="projectTimeline"
                  placeholder="e.g., Q2 2024, 6-month duration"
                  value={formData.projectTimeline}
                  onChange={handleInputChange}
                  className={fieldClass('projectTimeline')}
                />
              </div>

              <div>
                <label htmlFor="additionalInfo" className="block text-sm font-medium text-slate-700 mb-2">
                  Additional Technical Information
                </label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  rows={6}
                  maxLength={500}
                  value={formData.additionalInfo}
                  onChange={handleInputChange}
                  placeholder="Provide any additional technical details relevant to the inquiry (maximum 500 characters)"
                  className="w-full px-4 py-3 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-industrial-steel/30 focus:border-industrial-steel transition-colors resize-none"
                ></textarea>
                <p className="text-xs text-slate-500 mt-2">
                  {formData.additionalInfo.length} / 500 characters
                </p>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-dark-primary text-white px-8 py-4 rounded-md font-medium text-sm tracking-wide hover:bg-industrial-charcoal transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Technical Inquiry'}
              </button>
            </div>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-800">
                  Your inquiry has been submitted successfully. We will review and respond accordingly.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800">
                  An error occurred while submitting your inquiry. Please try again.
                </p>
              </div>
            )}

            {/* Confidentiality Notice */}
            <div className="pt-6 border-t border-slate-200">
              <p className="text-sm text-slate-600 leading-7">
                All information submitted is treated as confidential and reviewed internally prior to formal response.
              </p>
            </div>
          </form>

          {/* Technical Clarifications */}
          <div className="mt-24">
            <h2 className="font-serif text-2xl text-dark-primary mb-10 tracking-tight">
              Technical Clarifications
            </h2>
            <div className="space-y-0 divide-y divide-slate-200 border-t border-slate-200">

              <div className="py-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                <p className="text-sm font-medium text-dark-primary leading-6">
                  Does Arvakon provide certification services?
                </p>
                <p className="md:col-span-2 text-sm text-slate-600 leading-7">
                  No. Arvakon provides independent technical verification and reporting services. Certification, regulatory approval, and final acceptance authority remain with the designated certification body or equipment purchaser.
                </p>
              </div>

              <div className="py-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                <p className="text-sm font-medium text-dark-primary leading-6">
                  Does Arvakon act as a notified body under PED?
                </p>
                <p className="md:col-span-2 text-sm text-slate-600 leading-7">
                  No. Arvakon does not function as a notified body under PED 2014/68/EU and does not issue regulatory approvals.
                </p>
              </div>

              <div className="py-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                <p className="text-sm font-medium text-dark-primary leading-6">
                  Can Arvakon perform surveillance at international fabrication facilities?
                </p>
                <p className="md:col-span-2 text-sm text-slate-600 leading-7">
                  Yes. Services are structured to provide independent technical oversight at fabrication sites based on client-defined scope and applicable standards.
                </p>
              </div>

              <div className="py-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                <p className="text-sm font-medium text-dark-primary leading-6">
                  What documentation is provided after verification activities?
                </p>
                <p className="md:col-span-2 text-sm text-slate-600 leading-7">
                  Structured inspection reports, documented observations, traceability verification records, and testing witness documentation are provided in accordance with client requirements.
                </p>
              </div>

            </div>
          </div>

          {/* Procurement Documentation Requests */}
          <div className="mt-24">
            <h2 className="font-serif text-2xl text-dark-primary mb-10 tracking-tight">
              Procurement Documentation Requests
            </h2>
            <div className="space-y-6 border-t border-slate-200 pt-8">
              <p className="text-sm text-slate-600 leading-7">
                Arvakon may provide structured documentation summaries for procurement and vendor onboarding processes upon formal request.
              </p>
              <div>
                <p className="text-sm font-medium text-dark-primary mb-4 leading-6">
                  Available documentation may include:
                </p>
                <ul className="space-y-2">
                  <li className="text-sm text-slate-600 leading-7 pl-4 border-l border-slate-200">
                    Corporate capabilities summary
                  </li>
                  <li className="text-sm text-slate-600 leading-7 pl-4 border-l border-slate-200">
                    Standards reference framework overview
                  </li>
                  <li className="text-sm text-slate-600 leading-7 pl-4 border-l border-slate-200">
                    Service scope definition
                  </li>
                  <li className="text-sm text-slate-600 leading-7 pl-4 border-l border-slate-200">
                    Governance and independence statement
                  </li>
                </ul>
              </div>
              <p className="text-sm text-slate-600 leading-7">
                Requests for documentation should be submitted through the technical inquiry form above, specifying procurement documentation in the additional information field.
              </p>
              <p className="text-xs text-slate-400 leading-6">
                Documentation summaries are prepared in structured printable format suitable for vendor onboarding and internal procurement review.
              </p>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}
